// Bộ nhận dạng chữ viết tay NHỎ chạy ngay trên máy (không tốn tiền AI): 3 mạng MLP huấn luyện trên EMNIST —
// A/B/C/D · Đ/S · 0–9 (trọng số int8 ở /models/sheet-hw.json, ~0,4 MB, chỉ tải khi quét phiếu).
// Ô → bản đồ "mực" → tách ký tự theo vùng nét liền → chuẩn hoá 28×28 GIỐNG HỆT lúc huấn luyện
// (khung bao → vuông → 20×20 giữa 28×28) → phân loại. Dấu phẩy / dấu trừ nhận theo hình dạng.
import type { SheetGroup } from './sheetModel'

interface Net { classes: string[]; layers: { inN: number; outN: number; W: Float32Array; b: Float32Array }[] }
type Models = Record<'letters' | 'ds' | 'digits', Net>

let modelsP: Promise<Models> | null = null

function decode(raw: any): Net {
  return {
    classes: raw.classes,
    layers: raw.layers.map((L: any) => {
      const bin = atob(L.w)
      const W = new Float32Array(bin.length)
      for (let i = 0; i < bin.length; i++) { const v = bin.charCodeAt(i); W[i] = (v > 127 ? v - 256 : v) * L.scale }
      return { inN: L.in, outN: L.out, W, b: Float32Array.from(L.b) }
    }),
  }
}

export function loadModels(): Promise<Models> {
  if (!modelsP) {
    modelsP = fetch('/models/sheet-hw.json').then((r) => {
      if (!r.ok) throw new Error('Không tải được bộ nhận dạng chữ viết tay')
      return r.json()
    }).then((raw) => ({ letters: decode(raw.letters), ds: decode(raw.ds), digits: decode(raw.digits) }))
    modelsP.catch(() => { modelsP = null })
  }
  return modelsP
}

function predict(net: Net, x: Float32Array): { label: string; p: number } {
  let a = x
  net.layers.forEach((L, li) => {
    const out = Float32Array.from(L.b)
    for (let i = 0; i < L.inN; i++) {
      const v = a[i]
      if (!v) continue
      const row = i * L.outN
      for (let j = 0; j < L.outN; j++) out[j] += v * L.W[row + j]
    }
    if (li < net.layers.length - 1) for (let j = 0; j < out.length; j++) if (out[j] < 0) out[j] = 0
    a = out
  })
  if (a.length === 1) {                 // 2 lớp: scikit-learn dùng 1 đầu ra sigmoid (= xác suất lớp thứ 2)
    const p1 = 1 / (1 + Math.exp(-a[0]))
    return p1 >= 0.5 ? { label: net.classes[1], p: p1 } : { label: net.classes[0], p: 1 - p1 }
  }
  let mx = -Infinity, k = 0
  for (let j = 0; j < a.length; j++) if (a[j] > mx) { mx = a[j]; k = j }
  let sum = 0
  for (let j = 0; j < a.length; j++) sum += Math.exp(a[j] - mx)
  return { label: net.classes[k], p: 1 / sum }
}

// ── Ô → bản đồ mực (0..1) ở chiều cao 64px ──────────────────────────────────
const H = 64
interface Ink { ink: Float32Array; w: number; h: number }

function inkMap(cell: HTMLCanvasElement): Ink | null {
  const w = Math.max(8, Math.round(cell.width * H / cell.height)), h = H
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const ctx = c.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(cell, 0, 0, w, h)
  const d = ctx.getImageData(0, 0, w, h).data
  const lum = new Float32Array(w * h)
  for (let p = 0, i = 0; p < w * h; p++, i += 4) lum[p] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
  const sorted = Float32Array.from(lum).sort()
  const paper = sorted[Math.floor(sorted.length * 0.9)], dark = sorted[Math.floor(sorted.length * 0.02)]
  if (paper - dark < 25) return null
  const ink = new Float32Array(w * h)
  for (let p = 0; p < w * h; p++) ink[p] = Math.max(0, Math.min(1, (paper - lum[p]) / (paper - dark)))
  return { ink, w, h }
}

interface Comp { x0: number; y0: number; x1: number; y1: number; area: number; pix: number[] }

function components({ ink, w, h }: Ink): Comp[] {
  const on = new Uint8Array(w * h)
  for (let p = 0; p < w * h; p++) on[p] = ink[p] > 0.45 ? 1 : 0
  const seen = new Uint8Array(w * h), out: Comp[] = []
  for (let s = 0; s < w * h; s++) {
    if (!on[s] || seen[s]) continue
    const pix: number[] = [], stack = [s]
    seen[s] = 1
    let x0 = w, y0 = h, x1 = 0, y1 = 0
    while (stack.length) {
      const p = stack.pop()!, x = p % w, y = (p / w) | 0
      pix.push(p)
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y
      for (const q of [p - 1, p + 1, p - w, p + w, p - w - 1, p - w + 1, p + w - 1, p + w + 1]) {
        if (q < 0 || q >= w * h || seen[q] || !on[q]) continue
        if (Math.abs((q % w) - x) > 1) continue
        seen[q] = 1; stack.push(q)
      }
    }
    const bw = x1 - x0 + 1, bh = y1 - y0 + 1
    // bỏ đốm nhỏ + phần viền bảng sót ở mép ô (dải dài, mảnh, chạm mép)
    if (pix.length < w * h * 0.004) continue
    const edge = x0 === 0 || y0 === 0 || x1 === w - 1 || y1 === h - 1
    if (edge && ((bh > h * 0.75 && bw < w * 0.12) || (bw > w * 0.75 && bh < h * 0.15))) continue
    out.push({ x0, y0, x1, y1, area: pix.length, pix })
  }
  return out
}

/** Gộp các vùng nét chồng nhau theo chiều ngang thành ký tự (Đ = D + gạch; số 5 hai nét…). */
function glyphs(comps: Comp[]): Comp[] {
  const sorted = [...comps].sort((a, b) => a.x0 - b.x0)
  const out: Comp[] = []
  for (const c of sorted) {
    const last = out[out.length - 1]
    if (last) {
      const ov = Math.min(last.x1, c.x1) - Math.max(last.x0, c.x0)
      if (ov > Math.min(last.x1 - last.x0, c.x1 - c.x0) * 0.3) {
        last.x0 = Math.min(last.x0, c.x0); last.x1 = Math.max(last.x1, c.x1)
        last.y0 = Math.min(last.y0, c.y0); last.y1 = Math.max(last.y1, c.y1)
        last.area += c.area; last.pix = last.pix.concat(c.pix)
        continue
      }
    }
    out.push({ ...c, pix: [...c.pix] })
  }
  return out
}

/** Ký tự → vector 28×28 (giống norm28 lúc huấn luyện). Chỉ lấy mực của chính ký tự đó. */
function norm28(m: Ink, g: Comp): Float32Array {
  const bw = g.x1 - g.x0 + 1, bh = g.y1 - g.y0 + 1, s = Math.max(bw, bh)
  const sq = new Float32Array(s * s)
  const ox = Math.floor((s - bw) / 2), oy = Math.floor((s - bh) / 2)
  for (const p of g.pix) {
    const x = p % m.w, y = (p / m.w) | 0
    sq[(y - g.y0 + oy) * s + (x - g.x0 + ox)] = m.ink[p]
  }
  // nét mờ quanh viền ký tự (ink 0.2–0.45) cũng lấy để giống ảnh EMNIST
  for (let y = g.y0; y <= g.y1; y++) for (let x = g.x0; x <= g.x1; x++) {
    const v = m.ink[y * m.w + x], k = (y - g.y0 + oy) * s + (x - g.x0 + ox)
    if (v > 0.2 && v <= 0.45 && !sq[k]) sq[k] = v
  }
  const out = new Float32Array(784)
  const f = s / 20
  for (let Y = 0; Y < 20; Y++) for (let X = 0; X < 20; X++) {       // thu nhỏ trung bình vùng
    const sx0 = X * f, sx1 = (X + 1) * f, sy0 = Y * f, sy1 = (Y + 1) * f
    let acc = 0, wsum = 0
    for (let y = Math.floor(sy0); y < Math.ceil(sy1); y++) for (let x = Math.floor(sx0); x < Math.ceil(sx1); x++) {
      const wx = Math.min(x + 1, sx1) - Math.max(x, sx0), wy = Math.min(y + 1, sy1) - Math.max(y, sy0)
      if (wx <= 0 || wy <= 0 || x >= s || y >= s) continue
      acc += sq[y * s + x] * wx * wy; wsum += wx * wy
    }
    out[(Y + 4) * 28 + (X + 4)] = wsum ? Math.min(1, acc / wsum) : 0
  }
  return out
}

export interface HwReading { value: string; conf: number }

/** Đọc 1 ô bằng bộ nhận dạng trên máy. null = không tách được ký tự (để tầng sau xử lý). */
export function classifyCell(models: Models, group: SheetGroup, cell: HTMLCanvasElement): HwReading | null {
  const m = inkMap(cell)
  if (!m) return { value: '', conf: 0.9 }
  let gs = glyphs(components(m))
  if (!gs.length) return { value: '', conf: 0.8 }

  if (group === 'TN') {
    // một chữ: gộp mọi nét (bỏ vùng quá nhỏ so với nét lớn nhất)
    const big = Math.max(...gs.map((g) => g.area))
    const keep = gs.filter((g) => g.area >= big * 0.15)
    const all = keep.reduce((a, g) => ({ x0: Math.min(a.x0, g.x0), y0: Math.min(a.y0, g.y0), x1: Math.max(a.x1, g.x1), y1: Math.max(a.y1, g.y1), area: a.area + g.area, pix: a.pix.concat(g.pix) }))
    const r = predict(models.letters, norm28(m, all))
    return { value: r.label, conf: keep.length > 1 && keep.length !== gs.length ? r.p * 0.9 : r.p }
  }

  if (group === 'DS') {
    gs = gs.filter((g) => (g.y1 - g.y0) > m.h * 0.2)
    // đúng 4 ký tự: thừa → gộp 2 ký tự sát nhau nhất; thiếu → tách ký tự rộng nhất ở cột ít mực nhất
    while (gs.length > 4) {
      let k = 0, best = Infinity
      for (let i = 0; i < gs.length - 1; i++) { const gap = gs[i + 1].x0 - gs[i].x1; if (gap < best) { best = gap; k = i } }
      const a = gs[k], b = gs[k + 1]
      gs.splice(k, 2, { x0: a.x0, y0: Math.min(a.y0, b.y0), x1: b.x1, y1: Math.max(a.y1, b.y1), area: a.area + b.area, pix: a.pix.concat(b.pix) })
    }
    let guard = 0
    while (gs.length < 4 && guard++ < 4) {
      const widths = gs.map((g) => g.x1 - g.x0)
      const k = widths.indexOf(Math.max(...widths)), g = gs[k]
      const med = [...widths].sort((a, b) => a - b)[Math.floor(widths.length / 2)]
      if (widths[k] < Math.max(med * 1.4, m.h * 0.5)) break
      let cut = -1, low = Infinity
      for (let x = g.x0 + Math.round((g.x1 - g.x0) * 0.25); x <= g.x1 - Math.round((g.x1 - g.x0) * 0.25); x++) {
        let n = 0
        for (const p of g.pix) if (p % m.w === x) n++
        if (n < low) { low = n; cut = x }
      }
      const L = g.pix.filter((p) => p % m.w < cut), R = g.pix.filter((p) => p % m.w >= cut)
      const box = (pix: number[]) => {
        let x0 = m.w, y0 = m.h, x1 = 0, y1 = 0
        for (const p of pix) { const x = p % m.w, y = (p / m.w) | 0; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y }
        return { x0, y0, x1, y1, area: pix.length, pix }
      }
      gs.splice(k, 1, box(L), box(R))
    }
    if (gs.length !== 4) return null
    const rs = gs.map((g) => predict(models.ds, norm28(m, g)))
    return { value: rs.map((r) => r.label).join(''), conf: Math.min(...rs.map((r) => r.p)) * (guard ? 0.9 : 1) }
  }

  // TL: chữ số + dấu phẩy + dấu trừ. Tách DẤU PHẨY (nét nhỏ, ở nửa dưới) ra TRƯỚC khi gộp nét — phẩy viết
  // tay hay chồng dưới chữ số liền trước, gộp chung thì "6,3" thành "63".
  const raw = components(m)
  const tall = Math.max(...raw.map((g) => g.y1 - g.y0 + 1))
  const big = raw.filter((g) => g.y1 - g.y0 + 1 > tall * 0.6)
  const top = Math.min(...big.map((g) => g.y0)), bottom = Math.max(...big.map((g) => g.y1))
  const isComma = (g: Comp) => {
    const gh = g.y1 - g.y0 + 1, gw = g.x1 - g.x0 + 1, cy = (g.y0 + g.y1) / 2
    return gh < tall * 0.6 && gw < tall * 0.6 && g.y0 > top + (bottom - top) * 0.35 && cy > top + (bottom - top) * 0.55
  }
  const isMinus = (g: Comp) => {
    const gh = g.y1 - g.y0 + 1, gw = g.x1 - g.x0 + 1, cy = (g.y0 + g.y1) / 2
    return gh < tall * 0.35 && gw > gh * 1.5 && cy > top + (bottom - top) * 0.25 && cy < top + (bottom - top) * 0.75
  }
  const marks = raw.filter((g) => isComma(g) || isMinus(g))
  const digitsG = glyphs(raw.filter((g) => !marks.includes(g)))
  const items = [
    ...marks.map((g) => ({ x: (g.x0 + g.x1) / 2, g, mark: isComma(g) ? ',' : '-' })),
    ...digitsG.map((g) => ({ x: (g.x0 + g.x1) / 2, g, mark: '' })),
  ].sort((p, q) => p.x - q.x)
  let s = '', conf = 1
  for (const it of items) {
    if (it.mark === '-') { if (s) { conf *= 0.5; continue } s += '-'; continue }   // trừ chỉ ở đầu số
    if (it.mark === ',') { s += ','; continue }
    if (it.g.y1 - it.g.y0 + 1 < tall * 0.45) { conf *= 0.5; continue }           // vết lạ
    const r = predict(models.digits, norm28(m, it.g))
    s += r.label; conf = Math.min(conf, r.p)
  }
  if ((s.match(/,/g) || []).length > 1) conf *= 0.5
  return { value: s, conf }
}


